import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { RadarBubble, RadarZones } from "@/services/api/teacher";
import { teacherApi } from "@/services/api/teacher";
import type { RadarSizeConfig } from "@/components/radar/canvas/BubbleRenderer";
import { polarToCartesian } from "@/components/radar/utils/geometry";
import { resolveBadgeRingAngles } from "@/components/radar/ui/badgeRingLayout";
import { useAuthStore } from "@/store";
import {
  getAdminCollapsedTeachers,
  pruneCollapsedTeachers,
  setAdminCollapsedTeachers,
} from "@/utils/radarStorage";
import { useTalkT } from "./useTalkT";
import { talkRadarCodeDiscStyle, talkRadarDiscStyle } from "./talkRadarStage";
import {
  FROST_EDGE,
  FROST_GLYPH,
  radarBubbleFace,
  radarCardKind,
  radarDotZone,
  type RadarDotZone,
} from "./talkRadarBubble";
import { talkCodeHidden, teacherCodeMarks, type TeacherCodeMark } from "./talkRadarCodes";

export type RadarCardKind = "incoming" | "own";

export type RadarDot = {
  id: string;
  zone: RadarDotZone;
  left: string;
  top: string;
  requestId: string;
  requestIds: string[];
  incoming: boolean;
  kind: RadarCardKind;
  status: string | null;
  lessonDate: string | null;
  personId: string | null;
};

function radarRequestIds(bubble: RadarBubble): string[] {
  if (bubble.request_ids && bubble.request_ids.length > 0) return bubble.request_ids;
  if (bubble.request_id) return [bubble.request_id];
  return [];
}

function radarDotFrom(bubble: RadarBubble, index: number, reach = 46): RadarDot {
  const zone = radarDotZone(bubble);
  const rad = (((bubble.angle_degrees ?? 0) - 90) * Math.PI) / 180;
  const dist = Math.max(0, Math.min(1, bubble.distance_from_center ?? 0));
  const requestIds = radarRequestIds(bubble);
  return {
    id: bubble.id || `radar-dot-${index}`,
    zone,
    left: `${50 + Math.cos(rad) * dist * reach}%`,
    top: `${50 + Math.sin(rad) * dist * reach}%`,
    requestId: requestIds[0] || bubble.request_id || "",
    requestIds,
    incoming: bubble.bubble_type === "incoming",
    kind: radarCardKind(bubble.bubble_type),
    status: bubble.status || null,
    lessonDate: bubble.lesson_date || null,
    personId: bubble.requester_teacher_id || null,
  };
}

const CODE_REACH = 48;

function placeTeacherCodes(
  marks: TeacherCodeMark[],
  collapsed: ReadonlySet<string>,
  size: number
) {
  if (size <= 0) {
    return marks.map((mark) => {
      const rad = (((mark.angleDeg ?? 0) - 90) * Math.PI) / 180;
      return {
        ...mark,
        collapsed: collapsed.has(mark.teacherId),
        left: `${50 + Math.cos(rad) * CODE_REACH}%`,
        top: `${50 + Math.sin(rad) * CODE_REACH}%`,
      };
    });
  }
  const ringRadius = (size / 2) * (CODE_REACH / 50);
  const resolved = resolveBadgeRingAngles(
    marks.map((mark) => ({
      angleDeg: mark.angleDeg,
      halfWidthPx: collapsed.has(mark.teacherId) ? 18 : 24,
    })),
    ringRadius
  );
  return marks.map((mark, index) => {
    const pos = polarToCartesian(
      resolved[index] ?? mark.angleDeg,
      ringRadius,
      size / 2,
      size / 2
    );
    return {
      ...mark,
      collapsed: collapsed.has(mark.teacherId),
      left: `${(pos.x / size) * 100}%`,
      top: `${(pos.y / size) * 100}%`,
    };
  });
}

function useDiscRadius() {
  const ref = useRef<HTMLDivElement>(null);
  const [radius, setRadius] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => {
      const box = el.getBoundingClientRect();
      setRadius(Math.min(box.width, box.height) / 2);
    };
    apply();
    if (typeof ResizeObserver !== "function") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, radius };
}

function useRadarSizeConfig() {
  const [sizeConfig, setSizeConfig] = useState<RadarSizeConfig | null>(null);
  const [zones, setZones] = useState<RadarZones | null>(null);
  useEffect(() => {
    const load = teacherApi.getRadarConfig;
    if (typeof load !== "function") return;
    let live = true;
    load()
      .then((cfg) => {
        if (!live || !cfg) return;
        setSizeConfig({
          min_bubble_size: cfg.min_bubble_size,
          max_bubble_size: cfg.max_bubble_size,
          bubble_size_scale: cfg.bubble_size_scale,
        });
        if (cfg.zone_config) setZones(cfg.zone_config);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return { sizeConfig, zones };
}

function RadarDiscBubble({
  bubble,
  dot,
  discRadius,
  sizeConfig,
  zones,
  label,
  onOpen,
}: {
  bubble: RadarBubble;
  dot: RadarDot;
  discRadius: number;
  sizeConfig: RadarSizeConfig | null;
  zones: RadarZones | null;
  label: string;
  onOpen: (dot: RadarDot) => void;
}) {
  const face = radarBubbleFace(bubble, dot.zone, discRadius, sizeConfig, zones);
  return (
    <span
      className={`talk-radar-dot${face.incoming ? " is-incoming" : ""}${face.resolved ? " is-resolved" : ""}${face.frost ? " is-frost" : ""}`}
      role="img"
      aria-label={label}
      style={{
        ...face.bodyStyle,
        left: dot.left,
        top: dot.top,
        ["--hi-size" as string]: face.highlight ? `${face.highlight.size}px` : "0px",
        ["--hi-shift" as string]: face.highlight ? `${face.highlight.offset}px` : "0px",
        ["--tint-size" as string]: `${face.tintSize}px`,
      }}
      onClick={() => onOpen(dot)}
    >
      {face.attention && (
        <i
          className="talk-radar-ring"
          aria-hidden
          style={{
            width: face.attention.diameter,
            height: face.attention.diameter,
            borderColor: face.attention.color,
            animationDuration: `${face.attention.durationMs}ms`,
          }}
        />
      )}
      {face.prep && (
        <i
          className="talk-radar-prep"
          aria-hidden
          style={{
            width: face.prep.diameter,
            height: face.prep.diameter,
            animationDuration: `${face.prep.durationMs}ms`,
          }}
        />
      )}
      {face.frostRing && (
        <i
          className="talk-radar-frost-ring"
          aria-hidden
          style={{
            width: face.frostRing.diameter,
            height: face.frostRing.diameter,
            borderColor: face.frostRing.color,
          }}
        />
      )}
      {face.resolved && (
        <i
          className="talk-radar-resolved"
          aria-hidden
          style={{ width: face.resolved.outer, height: face.resolved.outer }}
        >
          <b style={{ width: face.resolved.inner, height: face.resolved.inner, background: face.color }} />
        </i>
      )}
      {face.frost && (
        <i
          className="talk-radar-frost"
          aria-hidden
          style={{
            background: face.frostVeil,
            color: FROST_GLYPH,
            borderColor: FROST_EDGE,
            borderWidth: face.frostEdge,
            fontSize: face.frostGlyph,
          }}
        >
          ❄
        </i>
      )}
      {face.badge && (
        <b
          className="talk-radar-badge"
          aria-hidden
          style={{
            width: face.badge.size,
            height: face.badge.size,
            left: `calc(50% + ${face.badge.offset}px)`,
            top: `calc(50% - ${face.badge.offset}px)`,
            fontSize: face.badge.font,
          }}
        >
          {face.badge.text}
        </b>
      )}
    </span>
  );
}

export function TalkRadarDisc({
  bubbles,
  onOpen,
  teacherCodes = false,
  allBubbles,
}: {
  bubbles: RadarBubble[];
  onOpen: (dot: RadarDot) => void;
  teacherCodes?: boolean;
  allBubbles?: RadarBubble[];
}) {
  const t = useTalkT();
  const userId = useAuthStore((s) => s.user?.id);
  const { ref, radius } = useDiscRadius();
  const { sizeConfig, zones } = useRadarSizeConfig();
  const [collapsedTeachers, setCollapsedTeachers] = useState<string[]>(() =>
    teacherCodes ? getAdminCollapsedTeachers(useAuthStore.getState().user?.id) : []
  );
  useEffect(() => {
    if (!teacherCodes) return;
    setCollapsedTeachers(getAdminCollapsedTeachers(userId));
  }, [teacherCodes, userId]);
  useEffect(() => {
    if (!teacherCodes) return;
    setAdminCollapsedTeachers(collapsedTeachers, userId);
  }, [collapsedTeachers, teacherCodes, userId]);
  useEffect(() => {
    if (!teacherCodes) return;
    const source = allBubbles ?? bubbles;
    const active = source
      .map((bubble) => bubble.requester_teacher_id)
      .filter((id): id is string => !!id);
    setCollapsedTeachers((prev) => pruneCollapsedTeachers(prev, active));
  }, [allBubbles, bubbles, teacherCodes]);

  const marks = useMemo(
    () => (teacherCodes ? teacherCodeMarks(bubbles) : []),
    [bubbles, teacherCodes]
  );
  const collapsedSet = useMemo(
    () => new Set(teacherCodes ? collapsedTeachers : []),
    [collapsedTeachers, teacherCodes]
  );
  const panicThreshold = zones?.panic_zone.threshold_hours ?? 2;
  const shown = teacherCodes
    ? bubbles.filter((bubble) => !talkCodeHidden(bubble, collapsedSet, panicThreshold))
    : bubbles;
  const placed = useMemo(
    () => placeTeacherCodes(marks, collapsedSet, radius * 2),
    [marks, collapsedSet, radius]
  );
  const bubbleReach = marks.length > 0 ? 34 : 46;

  function toggleTeacher(teacherId: string) {
    setCollapsedTeachers((prev) =>
      prev.includes(teacherId)
        ? prev.filter((id) => id !== teacherId)
        : [...prev, teacherId]
    );
  }

  return (
    <div
      className="talk-radar-disc"
      ref={ref}
      style={marks.length > 0 ? talkRadarCodeDiscStyle : talkRadarDiscStyle}
    >
      <span
        className="talk-radar-rings"
        role="img"
        aria-label={t("talk_radar_rings")}
      />
      <span
        className="talk-radar-arm"
        role="img"
        aria-label={t("talk_radar_sweep")}
      />
      {shown.map((bubble, index) => {
        const dot = radarDotFrom(bubble, index, bubbleReach);
        const label = t(
          dot.zone === "panic"
            ? "talk_radar_panic"
            : dot.zone === "middle"
              ? "talk_radar_middle"
              : "talk_radar_outer"
        );
        return (
          <RadarDiscBubble
            key={dot.id}
            bubble={bubble}
            dot={dot}
            discRadius={radius}
            sizeConfig={sizeConfig}
            zones={zones}
            label={label}
            onOpen={onOpen}
          />
        );
      })}
      {placed.map((mark) => {
        const stateKey = mark.collapsed
          ? "talk_radar_code_expand"
          : "talk_radar_code_collapse";
        return (
          <button
            key={mark.teacherId}
            type="button"
            className="talk-radar-code"
            aria-pressed={mark.collapsed}
            aria-label={t(stateKey, { code: mark.code, count: mark.bubbleCount })}
            title={mark.name}
            style={{ left: mark.left, top: mark.top }}
            onClick={() => toggleTeacher(mark.teacherId)}
          >
            {mark.collapsed ? (
              <>
                <b>{mark.bubbleCount}</b>
                <span>{mark.code}</span>
              </>
            ) : (
              mark.code
            )}
          </button>
        );
      })}
    </div>
  );
}
